import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { scrollToId } from "@/lib/lenis";
import { api } from "@/lib/api";
import { hasSupport } from "./support/Support";

const LINKS = [
  ["works", "Works"],
  ["comics", "Comics"],
  ["webtoons", "Webtoons"],
  ["timelapse", "Timelapse"],
  ["wip", "WIP"],
  ["latest", "Latest"],
  ["about", "About"],
  ["skills", "Skills"],
  ["support", "Support"],
  ["contact", "Contact"],
];

const Clock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <span data-testid="nav-clock" className="font-mono text-[11px] tracking-[0.2em] text-zinc-400">
      <span className="text-[#F5A524] blink">●</span> {now.toLocaleTimeString([], { hour12: false })}
    </span>
  );
};

export const Nav = ({ visible }) => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [hidden, setHidden] = useState([]);
  useEffect(() => {
    if (visible) return undefined;
    let alive = true;
    Promise.all(["webtoons", "videos", "wip"].map((p) => api.get(`/${p}`).then((r) => (Array.isArray(r.data) ? r.data.length : 0)).catch(() => 0))
      .concat(api.get("/projects").then((r) => (Array.isArray(r.data) ? r.data.filter((p) => p.category === "comic").length : 0)).catch(() => 0))
      .concat(api.get("/site").then((r) => (hasSupport(r.data) ? 1 : 0)).catch(() => 0)))
      .then(([w, v, p, c, s]) => { if (alive) setHidden([!w && "webtoons", !v && "timelapse", !p && "wip", !c && "comics", !s && "support"].filter(Boolean)); });
    return () => { alive = false; };
  }, [visible]);
  const links = visible ? LINKS.filter(([id]) => visible.includes(id)) : LINKS.filter(([id]) => !hidden.includes(id));

  const go = (id) => {
    setOpen(false);
    if (pathname !== "/") navigate("/", { state: { scrollTo: id } });
    else scrollToId(id);
  };

  return (
    <header data-testid="site-nav" className="fixed top-0 inset-x-0 z-50 bg-[#0B0C10]/80 backdrop-blur-md border-b border-[#F5A524]/20">
      <div className="flex items-center justify-between px-5 sm:px-10 h-[68px]">
        <Link to="/" data-testid="nav-logo-link" aria-label="Home"><Logo /></Link>
        <nav className="hidden xl:flex items-center">
          {links.map(([id, label], i) => (
            <button key={id} data-testid={`nav-link-${id}`} onClick={() => go(id)}
              className="group relative px-2 2xl:px-3 py-2 font-mono text-xs uppercase tracking-[0.12em] 2xl:tracking-[0.2em] text-zinc-300 hover:text-[#F5A524] transition-colors">
              <span className="text-[#F5A524]/50 mr-1">{String(i + 1).padStart(2, "0")}</span>
              <span className="opacity-0 group-hover:opacity-100 transition-opacity">[</span>{label}<span className="opacity-0 group-hover:opacity-100 transition-opacity">]</span>
            </button>
          ))}
        </nav>
        <div className="hidden 2xl:block"><Clock /></div>
        <button data-testid="nav-mobile-toggle" onClick={() => setOpen((o) => !o)} className="xl:hidden p-2 text-[#F5A524]" aria-label="Menu">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div data-testid="nav-mobile-menu" initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
            className="xl:hidden border-t border-[#F5A524]/20 bg-[#0B0C10] px-5 py-6 flex flex-col gap-3 max-h-[calc(100vh-68px)] overflow-y-auto" data-lenis-prevent>
            {links.map(([id, label], i) => (
              <button key={id} data-testid={`nav-mobile-link-${id}`} onClick={() => go(id)}
                className="text-left font-display font-black text-3xl uppercase text-white hover:text-[#F5A524]">
                <span className="font-mono text-xs text-[#F5A524] mr-3 align-middle">{String(i + 1).padStart(2, "0")}</span>{label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
